// API URL Configuration — uses VITE_API_URL, else falls back to the current
// origin (so a misconfigured build hits its own host, not a hardcoded prod URL).
export const MAIN_URL = import.meta.env.VITE_API_URL
  || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
const API_BASE_URL = `${MAIN_URL}/api`;

const API_TIMEOUT_MS = 30000; // 30 seconds - increased for better reliability

// List screens search, filter and sort client-side, so they need every record in one
// response rather than the API's default page of 15 (the backend caps this at 1000).
const LIST_PAGE_SIZE = 1000;

/** Build a list query string, dropping empty values and defaulting per_page to a full list. */
export const listQuery = (params?: string | Record<string, unknown>): string => {
  const qs = new URLSearchParams();
  if (typeof params === 'string') {
    new URLSearchParams(params).forEach((value, key) => qs.append(key, value));
  } else if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.append(key, String(value));
    });
  }
  if (!qs.has('per_page')) qs.set('per_page', String(LIST_PAGE_SIZE));
  return `?${qs.toString()}`;
};

// Get token from storage
const getToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('token');
  }
  return null;
};

/** A request that got no answer: it timed out, or the connection failed. */
type FetchFailure = Error & { kind?: 'timeout' | 'network' };

// Fetch with timeout helper
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    // Handle abort errors specifically
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw Object.assign(new Error('Request timed out. Please check your connection and try again.'), { kind: 'timeout' as const });
      }
      // fetch rejects with a TypeError when the connection fails ("Failed to fetch", Safari's "Load failed")
      if (error.name === 'TypeError') {
        console.error('Network error details:', error);
        throw Object.assign(new Error('Network error. Unable to connect to the server. Please check if the backend is running.'), { kind: 'network' as const });
      }
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Only reads are repeated automatically. */
const RETRYABLE_METHODS = ['GET', 'HEAD'];

const SAVE_TIMED_OUT = 'The server took too long to answer, so we stopped waiting. It may still have been saved: refresh the page and check before trying again.';
const SAVE_CONNECTION_LOST = 'The connection dropped before the server answered. It may still have been saved: refresh the page and check before trying again.';

// Retry wrapper with exponential backoff (GET/HEAD only)
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  timeoutMs: number,
  maxRetries: number = 2
): Promise<Response> {
  // Saves, updates and deletes are sent exactly once: repeating one after a timeout or a
  // lost answer could record it twice on the server (the same payment, a second event, …)
  const method = (options.method ?? 'GET').toUpperCase();
  if (!RETRYABLE_METHODS.includes(method)) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (error) {
      const kind = (error as FetchFailure)?.kind;
      if (kind === 'timeout') throw Object.assign(new Error(SAVE_TIMED_OUT), { kind });
      if (kind === 'network') throw Object.assign(new Error(SAVE_CONNECTION_LOST), { kind });
      throw error;
    }
  }

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchWithTimeout(url, options, timeoutMs);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      // Don't retry on authentication errors or 4xx status codes
      if (lastError.message.includes('Session expired') || 
          lastError.message.includes('Unauthorized')) {
        throw lastError;
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait with exponential backoff before retrying
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5 seconds
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

/** Error thrown by apiClient: the message plus the HTTP status and the server's JSON body. */
export type ApiError = Error & { status?: number; data?: any; errors?: Record<string, string[]> };

/** The server's own message for a failed request (first validation message when there is one). */
export function apiErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  const e = error as ApiError;
  const first = e?.errors ? Object.values(e.errors).flat()[0] : undefined;
  return first || e?.data?.message || e?.message || fallback;
}

// API client
export const apiClient = {
  async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getToken();
    
    // Check if body is FormData - if so, don't set Content-Type (browser will set it)
    const isFormData = options.body instanceof FormData;
    
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    
    // Always ask for JSON (otherwise Laravel answers a failed multipart request with a redirect);
    // only set Content-Type for non-FormData requests, the browser adds the multipart boundary.
    headers['Accept'] = 'application/json';
    if (!isFormData) {
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetchWithRetry(url, {
        ...options,
        headers,
      }, API_TIMEOUT_MS);
      
      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        localStorage.removeItem('token');
        // Only redirect to login if not already on login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please login again.');
      }
      
      // Parse response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json().catch(() => null);
      } else {
        // If not JSON, get the text to help debug
        const text = await response.text();
        console.error('Non-JSON response from', url, ':', text.substring(0, 200));
        throw new Error(`Server returned ${response.status}: Not JSON. Check if backend is running.`);
      }
      
      if (!response.ok) {
        // Laravel returns errors in { message: '...' } or { errors: {...} } format
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        if (response.status === 422) {
          // Validation error - format the errors nicely
          if (data?.errors && typeof data.errors === 'object') {
            const errorList = Object.entries(data.errors)
              .map(([field, messages]: [string, any]) => {
                const msgs = Array.isArray(messages) ? messages.join(', ') : messages;
                return `${field}: ${msgs}`;
              })
              .join(' | ');
            errorMessage = `Validation Error: ${errorList}`;
          } else {
            errorMessage = data?.message || 'Validation failed. Please check your input.';
          }
        } else {
          errorMessage = data?.message || data?.error || errorMessage;
        }
        
        // Keep the server's details for callers that need them (e.g. per-field errors)
        throw Object.assign(new Error(errorMessage), { status: response.status, data, errors: data?.errors });
      }
      
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  },
  
  get(endpoint: string) {
    return this.request(endpoint, { method: 'GET' });
  },
  
  post(endpoint: string, data: unknown, options?: { headers?: Record<string, string> }) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...(options?.headers && { headers: options.headers }),
    });
  },
  
  put(endpoint: string, data: unknown) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  delete(endpoint: string) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  },
  
  postFormData(endpoint: string, formData: FormData) {
    const token = getToken();
    // For FormData, we must NOT set Content-Type - browser sets it automatically with boundary
    // Only send Authorization header if token exists
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers,
    });
  },
};

// Auth API
export const authApi = {
  login: (credentials: { email?: string; phone?: string; password: string }) =>
    apiClient.post('/login', credentials),
  
  logout: () => apiClient.post('/logout', {}),
  
  getUser: () => apiClient.get('/user'),
  
  impersonate: (userId: number) => apiClient.post(`/admin/impersonate/${userId}`, {}),
};

// Users (Staff) API
export const usersApi = {
  list: (params?: { role?: string; status?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/users${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/users/${id}`),
  create: (data: { name: string; email: string; password?: string; role: string; phone?: string; status?: string }) =>
    apiClient.post('/users', data),
  update: (id: number, data: { name?: string; email?: string; role?: string; phone?: string; status?: string }) =>
    apiClient.put(`/users/${id}`, data),
  delete: (id: number) => apiClient.delete(`/users/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getOverview: () => apiClient.get('/dashboard'),
  getStats: (period: string = 'month') => apiClient.get(`/dashboard/stats?period=${period}`),
};

// Settings API
export const settingsApi = {
  list: () => apiClient.get('/settings'),
  get: (key: string) => apiClient.get(`/settings/${key}`),
  getAll: () => apiClient.get('/settings'),
  
  /**
   * Get public settings (logo, business info) - no authentication required
   * Used for homepage header/footer
   */
  getPublic: async () => {
    try {
      const response = await fetch(`${MAIN_URL}/api/frontend-api/settings`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        // Don't throw error for public endpoint failures - just return empty data
        console.warn(`Public settings API returned status ${response.status}`);
        return { success: false, data: null };
      }
      
      return await response.json();
    } catch (error) {
      // Return empty data instead of throwing to prevent app crashes
      console.error('Public settings API call failed:', error);
      return { success: false, data: null };
    }
  },
  
  update: (settings: Record<string, any>) => {
    // Convert settings object to array format expected by backend
    const settingsArray = Object.entries(settings).map(([key, value]) => {
      // Map logoUrl and faviconUrl to theme.logoUrl and theme.faviconUrl
      let finalKey = key;
      if (key === 'logoUrl') finalKey = 'theme.logoUrl';
      if (key === 'faviconUrl') finalKey = 'theme.faviconUrl';
      
      return {
        key: finalKey,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        type: typeof value === 'object' ? 'json' : 'string',
      };
    });
    return apiClient.post('/settings', { settings: settingsArray });
  },
  updateByKey: (key: string, data: any) => {
    // For nested settings like 'business', 'general', etc.
    // Convert the nested object to individual settings
    if (typeof data === 'object' && data !== null) {
      const settingsArray = Object.entries(data).map(([subKey, value]) => ({
        key: `${key}.${subKey}`,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        type: typeof value === 'object' ? 'json' : 'string',
      }));
      return apiClient.post('/settings', { settings: settingsArray });
    }
    return apiClient.put(`/settings/${key}`, { value: data, type: 'string' });
  },
  updateSingle: (key: string, data: { value: any; type?: string; description?: string }) =>
    apiClient.put(`/settings/${key}`, data),
};

// Quotations API
export const quotationsApi = {
  list: (params?: string) => apiClient.get(`/quotations${listQuery(params)}`),
  get: (id: number | string) => apiClient.get(`/quotations/${id}`),
  create: (data: unknown) => apiClient.post('/quotations', data),
  update: (id: number | string, data: unknown) => apiClient.put(`/quotations/${id}`, data),
  delete: (id: number | string) => apiClient.delete(`/quotations/${id}`),
  send: (id: number | string) => apiClient.post(`/quotations/${id}/send`, {}),
  updateStatus: (id: number | string, status: string) =>
    apiClient.post(`/quotations/${id}/update-status`, { status }),
  convertToEvent: (id: number | string, payload?: any) => apiClient.post(`/quotations/${id}/convert-to-event`, payload || {}),
  clone: (id: number | string) => apiClient.post(`/quotations/${id}/clone`, {}),
  getLogs: (id: number | string) => apiClient.get(`/quotations/${id}/logs`),
};

// Clients API
export const clientsApi = {
  list: (params?: string) => apiClient.get(`/clients${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/clients/${id}`),
  create: (data: unknown) => apiClient.post('/clients', data),
  update: (id: number, data: unknown) => apiClient.put(`/clients/${id}`, data),
  delete: (id: number) => apiClient.delete(`/clients/${id}`),
  getEvents: (id: number) => apiClient.get(`/clients/${id}/events`),
  getPayments: (id: number) => apiClient.get(`/clients/${id}/payments`),
  getLedger: (id: number, params?: string) => apiClient.get(`/clients/${id}/ledger${params ? `?${params}` : ''}`),
};

// Events API
export const eventsApi = {
  list: (params?: string) => apiClient.get(`/events${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/events/${id}`),
  create: (data: unknown) => apiClient.post('/events', data),
  createPastEvent: (data: unknown) => apiClient.post('/events/past', data),
  update: (id: number, data: unknown) => apiClient.put(`/events/${id}`, data),
  delete: (id: number) => apiClient.delete(`/events/${id}`),
  updateStatus: (id: number, status: string) =>
    apiClient.post(`/events/${id}/update-status`, { status }),
  addPayment: (id: number, data: unknown) =>
    apiClient.post(`/events/${id}/add-payment`, data),
  getFinancials: (id: number) => apiClient.get(`/events/${id}/financials`),
  getInvoices: (id: number) => apiClient.get(`/events/${id}/invoices`),
  getTasks: (id: number) => apiClient.get(`/tasks${listQuery({ event_id: id })}`),
  getCalendar: (params?: string) => apiClient.get(`/events/calendar${params || ''}`),
  getVendorPaymentSummary: (id: number) => apiClient.get(`/events/${id}/vendor-payments/summary`),
  getVendorPayments: (id: number) => apiClient.get(`/events/${id}/vendor-payments`),
  addVendorPayment: (id: number, data: unknown) => apiClient.post(`/events/${id}/vendor-payments`, data),
  deleteVendorPayment: (eventId: number, paymentId: number) => apiClient.delete(`/events/${eventId}/vendor-payments/${paymentId}`),
  getLogs: (id: number) => apiClient.get(`/events/${id}/logs`),
  // Event Vendors (assign vendors to events)
  addEventVendor: (eventId: number, data: unknown) => apiClient.post(`/events/${eventId}/vendors`, data),
  removeEventVendor: (eventId: number, vendorId: number) => apiClient.delete(`/events/${eventId}/vendors/${vendorId}`),
  // Event Services (add services to events)
  addEventService: (eventId: number, data: unknown) => apiClient.post(`/events/${eventId}/services`, data),
  removeEventService: (eventId: number, serviceId: number) => apiClient.delete(`/events/${eventId}/services/${serviceId}`),
  // Room Bookings for events
  getRoomBookings: (eventId: number) => apiClient.get(`/room-bookings${listQuery({ event_id: eventId })}`),
  addRoomBooking: (eventId: number, data: unknown) => apiClient.post('/room-bookings', { ...data as object, event_id: eventId }),
  removeRoomBooking: (bookingId: number) => apiClient.delete(`/room-bookings/${bookingId}`),
  updateRoomBooking: (bookingId: number, data: unknown) => apiClient.put(`/room-bookings/${bookingId}`, data),
};

// Vendors API
export const vendorsApi = {
  list: (params?: string) => apiClient.get(`/vendors${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/vendors/${id}`),
  create: (data: unknown) => apiClient.post('/vendors', data),
  update: (id: number, data: unknown) => apiClient.put(`/vendors/${id}`, data),
  delete: (id: number) => apiClient.delete(`/vendors/${id}`),
  getEvents: (id: number) => apiClient.get(`/vendors/${id}/events`),
  getPayments: (id: number) => apiClient.get(`/vendors/${id}/payments`),
  getLedger: (id: number) => apiClient.get(`/vendors/${id}/ledger`),
  getAllPayments: (params?: { vendor_id?: number; per_page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/vendors/payments/all${queryParams ? `?${queryParams}` : ''}`);
  },
};

// Tasks API
export const tasksApi = {
  list: (params?: string) => apiClient.get(`/tasks${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/tasks/${id}`),
  create: (data: unknown) => apiClient.post('/tasks', data),
  update: (id: number, data: unknown) => apiClient.put(`/tasks/${id}`, data),
  delete: (id: number) => apiClient.delete(`/tasks/${id}`),
  complete: (id: number) => apiClient.post(`/tasks/${id}/complete`, {}),
  getMyTasks: () => apiClient.get(`/tasks/my-tasks${listQuery()}`),
};

// Invoices API
export const invoicesApi = {
  list: (params?: { status?: string; from_date?: string; to_date?: string; search?: string; per_page?: number; page?: number }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/invoices${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/invoices/${id}`),
  create: (data: any) => apiClient.post('/invoices', data),
  generateFromEvent: (eventId: number, data?: { notes?: string }) => 
    apiClient.post(`/invoices/generate/${eventId}`, data || {}),
  update: (id: number, data: any) => apiClient.put(`/invoices/${id}`, data),
  delete: (id: number) => apiClient.delete(`/invoices/${id}`),
  download: async (id: number) => {
    // Fetch with the auth token (window.open would omit it and 401), then save the blob.
    const token = getToken();
    const url = `${API_BASE_URL}/invoices/${id}/download`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error('Failed to download invoice');
    }
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `invoice-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  },
  markAsPaid: (id: number) => apiClient.post(`/invoices/${id}/mark-paid`, {}),
  recalculate: (id: number) => apiClient.post(`/invoices/${id}/recalculate`, {}),
};

// Invoice Settings API
export const invoiceSettingsApi = {
  get: () => apiClient.get('/invoice-settings'),
  update: (data: any) => apiClient.put('/invoice-settings', data),
  resetAllNumbers: () => apiClient.post('/invoices/reset-all-numbers', {}),
};

// Quotation Settings API
export const quotationSettingsApi = {
  get: () => apiClient.get('/quotation-settings'),
  update: (data: any) => apiClient.put('/quotation-settings', data),
  resetAllNumbers: () => apiClient.post('/quotations/reset-all-numbers', {}),
};

// Expenses API
export const expensesApi = {
  list: (params?: string) => apiClient.get(`/expenses${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/expenses/${id}`),
  create: (data: unknown) => apiClient.post('/expenses', data),
  update: (id: number, data: unknown) => apiClient.put(`/expenses/${id}`, data),
  delete: (id: number) => apiClient.delete(`/expenses/${id}`),
  getCategories: () => apiClient.get('/expenses/categories/list'),
  /** Summary figures for the Expenses page (vendor-payment copies excluded) */
  summary: () => apiClient.get('/expenses/summary'),
  /** Categories (most used first), payment methods and the receipt rule for the expense form */
  formOptions: () => apiClient.get('/expenses/form-options'),
  /** Create a category from the expense form; 422 with `existing` for a near-duplicate name */
  createCategory: (data: { name: string; description?: string }) => apiClient.post('/expenses/categories', data),
  /** Create (no id) or update an expense with an optional receipt file */
  save: (id: number | undefined, form: FormData) =>
    id ? apiClient.postFormData(`/expenses/${id}?_method=PUT`, form) : apiClient.postFormData('/expenses', form),
};

// Enquiries API
export const enquiriesApi = {
  list: (params?: string) => apiClient.get(`/enquiries${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/enquiries/${id}`),
  create: (data: unknown) => apiClient.post('/enquiries', data),
  update: (id: number, data: unknown) => apiClient.put(`/enquiries/${id}`, data),
  delete: (id: number) => apiClient.delete(`/enquiries/${id}`),
  /** After saving a quotation started from this enquiry: links it and marks the enquiry converted */
  linkQuotation: (id: number, quotationId: number) =>
    apiClient.post(`/enquiries/${id}/link-quotation`, { quotation_id: quotationId }),
};

// Masters API
export const mastersApi = {
  getAll: () => apiClient.get('/masters/all'),
  
  // Expense Categories
  expenseCategories: {
    list: () => apiClient.get('/masters/expense-categories'),
    create: (data: unknown) => apiClient.post('/masters/expense-categories', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/expense-categories/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/expense-categories/${id}`),
    /** Move every expense of `id` into `targetId`, then delete `id` */
    merge: (id: number, targetId: number) => apiClient.post(`/masters/expense-categories/${id}/merge`, { target_id: targetId }),
  },
  
  // Vendor Types
  vendorTypes: {
    list: () => apiClient.get('/masters/vendor-types'),
    create: (data: unknown) => apiClient.post('/masters/vendor-types', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/vendor-types/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/vendor-types/${id}`),
  },
  
  // Services
  services: {
    /** Pass { active_only: 1 } in pickers so archived services can't be added */
    list: (params?: { active_only?: 1; category?: string; search?: string }) => apiClient.get(`/masters/services${params ? `?${new URLSearchParams(params as Record<string, string>)}` : ''}`),
    create: (data: unknown) => apiClient.post('/masters/services', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/services/${id}`, data),
    /** Create (no id) or update with an optional photo (image_file) and update_packages flag */
    save: (id: number | undefined, form: FormData) =>
      id ? apiClient.postFormData(`/masters/services/${id}?_method=PUT`, form) : apiClient.postFormData('/masters/services', form),
    delete: (id: number) => apiClient.delete(`/masters/services/${id}`),
  },
  
  // Packages
  packages: {
    /** Pass { active_only: 1 } in pickers so archived packages aren't offered */
    list: (params?: { active_only?: 1 }) => apiClient.get(`/masters/packages${params ? `?${new URLSearchParams(params as Record<string, string>)}` : ''}`),
    create: (data: unknown) => apiClient.post('/masters/packages', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/packages/${id}`, data),
    /** Copy with its lines; the copy starts archived */
    duplicate: (id: number) => apiClient.post(`/masters/packages/${id}/duplicate`, {}),
    delete: (id: number) => apiClient.delete(`/masters/packages/${id}`),
  },
  
  // Rooms (each room also carries current_booking / next_booking worked out from its bookings)
  rooms: {
    list: () => apiClient.get('/masters/rooms'),
    create: (data: unknown) => apiClient.post('/masters/rooms', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/rooms/${id}`, data),
    /** "available" (in service) or "maintenance" (out of service) */
    setStatus: (id: number, status: 'available' | 'maintenance') => apiClient.put(`/masters/rooms/${id}`, { status }),
    delete: (id: number) => apiClient.delete(`/masters/rooms/${id}`),
  },

  // Vendors
  vendors: {
    list: () => apiClient.get('/masters/vendors'),
    create: (data: unknown) => apiClient.post('/masters/vendors', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/vendors/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/vendors/${id}`),
  },
  
  // Venues
  venues: {
    /**
     * Active venues only by default (what pickers should offer): archived venues stay on
     * old events but aren't offered for new ones. The Venues page passes includeArchived.
     */
    list: (params?: { includeArchived?: boolean }) =>
      apiClient.get(`/venues${listQuery(params?.includeArchived ? undefined : { active: 1 })}`),
    /** Archive (hide from pickers, keep on old events) or restore a venue */
    setActive: (id: number, isActive: boolean) => apiClient.put(`/venues/${id}`, { is_active: isActive }),
    create: (data: unknown) => apiClient.post('/venues', data),
    update: (id: number, data: unknown) => apiClient.put(`/venues/${id}`, data),
    delete: (id: number) => apiClient.delete(`/venues/${id}`),
    getAvailability: (id: number, fromDate: string, toDate: string) =>
      apiClient.get(`/venues/${id}/availability?from_date=${fromDate}&to_date=${toDate}`),
    checkAllAvailability: (startDate: string, endDate: string) =>
      apiClient.get(`/availability/venues?start_date=${startDate}&end_date=${endDate}`),
  },

  // Vendor Payments
  vendorPayments: {
    list: (params?: { 
      from_date?: string; 
      to_date?: string; 
      vendor_id?: number;
      vendor_type_id?: number;
      payment_method?: string;
      event_id?: number;
      per_page?: number;
      page?: number;
    }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/reports/vendor-payments${queryParams ? `?${queryParams}` : ''}`);
    },
    summary: (params?: { from_date?: string; to_date?: string; vendor_id?: number }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/reports/vendor-payments/summary${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/vendors/payments', data),
    delete: (id: number) => apiClient.delete(`/vendor-payments/${id}`),
  },

  // Event Types
  eventTypes: {
    list: (params?: { status?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/masters/event-types${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/masters/event-types', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/event-types/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/event-types/${id}`),
  },

  // Payment Methods
  paymentMethods: {
    list: (params?: { status?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/masters/payment-methods${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/masters/payment-methods', data),
    update: (id: number, data: unknown) => apiClient.put(`/masters/payment-methods/${id}`, data),
    delete: (id: number) => apiClient.delete(`/masters/payment-methods/${id}`),
  },

  // Testimonials
  testimonials: {
    list: (params?: { is_active?: boolean; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/testimonials/all${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (data: unknown) => apiClient.post('/testimonials', data),
    update: (id: number, data: unknown) => apiClient.put(`/testimonials/${id}`, data),
    delete: (id: number) => apiClient.delete(`/testimonials/${id}`),
  },

  // Room Bookings
  roomBookings: {
    list: (params?: { event_id?: number; room_id?: number; status?: string; from_date?: string; to_date?: string }) =>
      apiClient.get(`/room-bookings${listQuery(params)}`),
    /** Light event list (no financials) for the booking form's event picker */
    eventOptions: () => apiClient.get('/room-bookings/event-options'),
    create: (data: unknown) => apiClient.post('/room-bookings', data),
    update: (id: number, data: unknown) => apiClient.put(`/room-bookings/${id}`, data),
    /** booked → checked_in → checked_out, or cancelled (releases the room) */
    setStatus: (id: number, status: string) => apiClient.put(`/room-bookings/${id}`, { status }),
    delete: (id: number) => apiClient.delete(`/room-bookings/${id}`),
  },

  // Activity Logs
  activityLogs: {
    list: (params?: { user_id?: number; action?: string; from_date?: string; to_date?: string; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/activity-logs${queryParams ? `?${queryParams}` : ''}`);
    },
    stats: (params?: { from_date?: string; to_date?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/activity-logs/stats${queryParams ? `?${queryParams}` : ''}`);
    },
    actions: () => apiClient.get('/activity-logs/actions'),
  },

  // Payment Logs
  paymentLogs: {
    list: (params?: { payment_type?: string; action?: string; client_id?: number; vendor_id?: number; event_id?: number; from_date?: string; to_date?: string; search?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/payment-logs${queryParams ? `?${queryParams}` : ''}`);
    },
    stats: (params?: { from_date?: string; to_date?: string }) => {
      const queryParams = new URLSearchParams(params as any).toString();
      return apiClient.get(`/payment-logs/stats${queryParams ? `?${queryParams}` : ''}`);
    },
    actions: () => apiClient.get('/payment-logs/actions'),
  },
};

// Testimonials API (standalone export)
export const testimonialsApi = {
  list: (params?: { is_active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/testimonials/all${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/testimonials', data),
  update: (id: number, data: unknown) => apiClient.put(`/testimonials/${id}`, data),
  delete: (id: number) => apiClient.delete(`/testimonials/${id}`),
};

// Client Payments API
export const clientPaymentsApi = {
  list: (params?: { client_id?: number; event_id?: number; from_date?: string; to_date?: string }) =>
    apiClient.get(`/client-payments${listQuery(params)}`),
  get: (id: number) => apiClient.get(`/client-payments/${id}`),
  create: (data: any) => apiClient.post('/client-payments', data),
  update: (id: number, data: any) => apiClient.put(`/client-payments/${id}`, data),
  delete: (id: number) => apiClient.delete(`/client-payments/${id}`),
};

// Reports API
/** Report params: empty values are dropped, arrays are sent as key[]=a&key[]=b. */
type ReportParams = Record<string, string | number | boolean | null | undefined | Array<string | number>>;
const reportUrl = (path: string, params?: ReportParams): string => {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((v) => qs.append(`${key}[]`, String(v)));
    else if (value !== undefined && value !== null && value !== '') qs.append(key, String(value));
  });
  const query = qs.toString();
  return `/reports/${path}${query ? `?${query}` : ''}`;
};
type ReportRange = { from_date?: string; to_date?: string };

export const reportsApi = {
  financialSummary: (params?: ReportRange) => apiClient.get(reportUrl('financial-summary', params)),
  vendorPayments: (params?: ReportRange & { vendor_id?: number }) => apiClient.get(reportUrl('vendor-payments', params)),
  clientPayments: (params?: ReportRange & { client_id?: number }) => apiClient.get(reportUrl('client-payments', params)),
  expenseBreakdown: (params?: ReportRange) => apiClient.get(reportUrl('expense-breakdown', params)),
  // Vendor Reports
  vendorPerformance: (params?: ReportRange & { vendor_id?: number; vendor_type_id?: number }) =>
    apiClient.get(reportUrl('vendor-performance', params)),
  vendorAssignments: (params?: ReportRange & { vendor_id?: number; status?: string; per_page?: number; page?: number }) =>
    apiClient.get(reportUrl('vendor-assignments', params)),
  vendorComparison: (params?: ReportRange & { vendor_ids?: number[]; vendor_type_id?: number }) =>
    apiClient.get(reportUrl('vendor-comparison', params)),
  vendorCommissions: (params?: ReportRange & { vendor_id?: number; commission_type?: string; per_page?: number; page?: number }) =>
    apiClient.get(reportUrl('vendor-commissions', params)),
  /** Events report: one page of rows plus totals for the whole filtered set. */
  eventsReport: (params?: ReportRange & {
    status?: string; event_type?: string; venue_id?: number | string; search?: string;
    sort?: string; page?: number; per_page?: number;
  }) => apiClient.get(reportUrl('events', params)),
  eventDetailReport: (eventId: number) => apiClient.get(`/reports/event-detail/${eventId}`),
  revenueTrends: (params?: ReportRange & { year?: number }) => apiClient.get(reportUrl('revenue-trends', params)),
  clientAnalytics: (params?: ReportRange) => apiClient.get(reportUrl('client-analytics', params)),
  enquiryFunnel: (params?: ReportRange) => apiClient.get(reportUrl('enquiry-funnel', params)),
  packageAnalytics: (params?: ReportRange) => apiClient.get(reportUrl('package-analytics', params)),
  venueUtilization: (params?: ReportRange) => apiClient.get(reportUrl('venue-utilization', params)),
  roomRevenue: (params?: ReportRange) => apiClient.get(reportUrl('room-revenue', params)),
  /** Money clients owe today, grouped by how late it is. */
  receivables: () => apiClient.get('/reports/receivables'),
  /** Monthly profit & loss by event month, compared with a year earlier. */
  profitLoss: (params?: ReportRange) => apiClient.get(reportUrl('profit-loss', params)),
  /** Invoices with GST, by invoice date or event date. */
  gst: (params?: ReportRange & { basis?: 'invoice' | 'event' }) => apiClient.get(reportUrl('gst', params)),
};

// System Console API (super admin): server maintenance commands, their history and the error log
const SYSTEM_RUN_TIMEOUT_MS = 6 * 60 * 1000;

export const systemConsoleApi = {
  commands: () => apiClient.get('/system-console'),
  status: () => apiClient.get('/system-console/status'),
  history: () => apiClient.get('/system-console/history'),
  log: (params: { limit: number; errors: boolean }) =>
    apiClient.get(`/system-console/log?limit=${params.limit}&errors=${params.errors ? 1 : 0}`),
  clearLog: () => apiClient.post('/system-console/log/clear', {}),
  /**
   * Runs one command. Sent exactly once, without the usual 30-second timeout and automatic
   * retries: a database update can take minutes and must never be repeated by itself.
   */
  run: async (command: string, confirm?: string | null) => {
    const token = getToken();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SYSTEM_RUN_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_BASE_URL}/system-console/run`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ command, confirm: confirm ?? null }),
        signal: controller.signal,
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const message = data?.message
          || (res.status === 502 || res.status === 504
            ? 'The server took too long to answer. The command may still have finished: check History.'
            : `The server answered with an error (${res.status}).`);
        throw Object.assign(new Error(message), { status: res.status });
      }
      return data;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('No answer after 6 minutes. The command may still be running: check History.');
      }
      if (error instanceof TypeError) {
        throw new Error('Could not reach the server. The command may still have run: check History.');
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  },
  /** Saves the server's error log file (fetched with the login token). */
  downloadLog: async () => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/system-console/log/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error(res.status === 404 ? 'There is no log file yet.' : 'Could not download the log.');
    }
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = 'laravel.log';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  },
};

// Event Types API
export const eventTypesApi = {
  list: (params?: { status?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/masters/event-types${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/masters/event-types', data),
  update: (id: number, data: unknown) => apiClient.put(`/masters/event-types/${id}`, data),
  delete: (id: number) => apiClient.delete(`/masters/event-types/${id}`),
};

// Payment Methods API
export const paymentMethodsApi = {
  list: (params?: { status?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/masters/payment-methods${queryParams ? `?${queryParams}` : ''}`);
  },
  create: (data: unknown) => apiClient.post('/masters/payment-methods', data),
  update: (id: number, data: unknown) => apiClient.put(`/masters/payment-methods/${id}`, data),
  delete: (id: number) => apiClient.delete(`/masters/payment-methods/${id}`),
};

// Image Upload API
export const imageApi = {
  upload: async (file: File, type: 'logo' | 'favicon' | 'gallery' | 'pwa', settingKey?: string) => {
    const formData = new FormData();
    // Ensure the file has a proper name with extension
    const fileName = file.name || `${type}_${Date.now()}.jpg`;
    formData.append('image', file, fileName);
    formData.append('type', type);
    if (settingKey) {
      formData.append('setting_key', settingKey);
    }

    const token = getToken();
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetchWithRetry(
        `${API_BASE_URL}/upload/image`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
        API_TIMEOUT_MS
      );

      if (response.status === 401) {
        localStorage.removeItem('token');
        // Only redirect to login if not already on login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please login again.');
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || 'Upload failed');
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Upload failed');
    }
  },

  delete: (path: string, settingKey?: string) => {
    const params = new URLSearchParams();
    params.append('path', path);
    if (settingKey) {
      params.append('setting_key', settingKey);
    }
    return apiClient.delete(`/upload/image?${params.toString()}`);
  },
};

// FAQs API
export const faqsApi = {
  list: (params?: { category?: string; is_active?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/faqs${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/faqs/${id}`),
  create: (data: unknown) => apiClient.post('/faqs', data),
  update: (id: number, data: unknown) => apiClient.put(`/faqs/${id}`, data),
  delete: (id: number) => apiClient.delete(`/faqs/${id}`),
  getCategories: () => apiClient.get('/faqs/categories/list'),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/faqs/update-order', { items }),
  toggleActive: (id: number) => apiClient.post(`/faqs/${id}/toggle-active`, {}),
};

// Features API
export const featuresApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/features${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/features/${id}`),
  create: (data: unknown) => apiClient.post('/features', data),
  update: (id: number, data: unknown) => apiClient.put(`/features/${id}`, data),
  delete: (id: number) => apiClient.delete(`/features/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/features/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/features/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/features/${id}/toggle-active`, {}),
};

// Galleries API
export const galleriesApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/galleries${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/galleries/${id}`),
  create: (data: unknown) => apiClient.post('/galleries', data),
  update: (id: number, data: unknown) => apiClient.put(`/galleries/${id}`, data),
  delete: (id: number) => apiClient.delete(`/galleries/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/galleries/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/galleries/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/galleries/${id}/toggle-active`, {}),
};

// Instagram Reels API
export const instagramReelsApi = {
  list: (params?: { is_active?: string; is_selected?: string; search?: string }) => {
    const queryParams = new URLSearchParams(params as any).toString();
    return apiClient.get(`/instagram-reels${queryParams ? `?${queryParams}` : ''}`);
  },
  get: (id: number) => apiClient.get(`/instagram-reels/${id}`),
  create: (data: unknown) => apiClient.post('/instagram-reels', data),
  update: (id: number, data: unknown) => apiClient.put(`/instagram-reels/${id}`, data),
  delete: (id: number) => apiClient.delete(`/instagram-reels/${id}`),
  updateOrder: (items: { id: number; sort_order: number }[]) =>
    apiClient.post('/instagram-reels/update-order', { items }),
  toggleSelected: (id: number) => apiClient.post(`/instagram-reels/${id}/toggle-selected`, {}),
  toggleActive: (id: number) => apiClient.post(`/instagram-reels/${id}/toggle-active`, {}),
};

// Profile API
export const profileApi = {
  get: () => apiClient.get('/profile'),
  update: (data: { name?: string; email?: string }) => apiClient.put('/profile', data),
  changePassword: (data: { current_password: string; password: string; password_confirmation: string }) =>
    apiClient.post('/profile/change-password', data),
};

// Health check for backend availability
export const healthApi = {
  check: async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      }, 5000); // 5 second timeout for health check
      
      return response.ok;
    } catch (error) {
      return false;
    }
  },
};

// Data Management API
export const dataManagementApi = {
  /**
   * Get summary of all data counts
   */
  getSummary: () => apiClient.get('/data/summary'),
  
  /**
   * Get detailed database statistics
   */
  getStats: () => apiClient.get('/data/stats'),
  
  /**
   * Reset data. The server saves a backup first and only accepts the typed word RESET.
   * @param type - 'operational' (bookings, customers, money) or 'all' (also venues, vendors, services, packages)
   * @param confirmText - what the person typed in the confirmation box
   */
  resetData: (type: 'all' | 'operational', confirmText: string) =>
    apiClient.post('/data/reset', { type, confirm_text: confirmText }),
  
  /**
   * Generate dummy data for testing
   * @param count - Number of records to generate (optional)
   */
  generateDummyData: (count?: number) =>
    apiClient.post('/data/generate-dummy', count ? { count } : {}),
  
  /**
   * Export all data to JSON (chunked for large datasets)
   * @param table - Optional: export specific table only
   * @param chunkSize - Number of records per chunk (default: 1000)
   */
  exportData: (table?: string, chunkSize?: number) => {
    const params = new URLSearchParams();
    if (table) params.append('table', table);
    if (chunkSize) params.append('chunk_size', chunkSize.toString());
    const query = params.toString();
    return apiClient.get(`/data/export${query ? `?${query}` : ''}`);
  },
  
  /**
   * Import data from JSON (batched for large datasets)
   * @param data - The data to import
   * @param mergeStrategy - How to handle conflicts: 'replace', 'merge', or 'skip'
   * @param batchSize - Number of records per batch (default: 500)
   */
  importData: (data: any, mergeStrategy: 'replace' | 'merge' | 'skip' = 'skip', batchSize?: number) =>
    apiClient.post('/data/import', { data, merge_strategy: mergeStrategy, batch_size: batchSize }),
};

// Site Settings API (Frontend Website Settings)
export const siteSettingsApi = {
  /**
   * Get site settings
   */
  get: () => apiClient.get('/site-settings'),
  
  /**
   * Update site settings
   */
  update: (data: {
    hero_title?: string;
    hero_subtitle?: string;
    hero_images?: string[];
    stats_capacity?: string;
    stats_events?: string;
    stats_experience?: string;
    stats_satisfaction?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_address?: string;
    social_facebook?: string;
    social_instagram?: string;
    social_twitter?: string;
    footer_about?: string;
  }) => apiClient.put('/site-settings', data),
  
  /**
   * Upload hero image
   */
  uploadHeroImage: (formData: FormData) => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return apiClient.request('/site-settings/hero-image', {
      method: 'POST',
      body: formData,
      headers,
    });
  },
  
  /**
   * Delete hero image
   */
  deleteHeroImage: (url: string) => apiClient.delete(`/site-settings/hero-image?url=${encodeURIComponent(url)}`),
  
  /**
   * Get public site settings (no auth required)
   */
  getPublic: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-settings/public`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch public site settings:', error);
      throw error;
    }
  },
};

export const rolePageAccessApi = {
  me: () => apiClient.get(`/role-access/me`),
  index: () => apiClient.get(`/role-access`),
  update: (role: string, access: Record<string, boolean>) =>
    apiClient.put(`/role-access`, { role, access }),
};
